import { useState, useEffect } from 'react'
import { Modal, Form, Space, Typography, Tag, Card, Switch, message, Button } from 'antd'
import { EyeOutlined, EditOutlined } from '@ant-design/icons'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { featureAssignmentsApi, type FeatureAssignment } from '@/api/feature-assignments'
import { FeatureValueEditor } from './FeatureValueEditor'
import { FeaturePreview } from './FeaturePreview'
import type { PreviewFeatureResponse } from '@/api/feature-assignments'

const { Text } = Typography

// Helper to extract error message from API error response
function getErrorMessage(error: unknown, defaultMessage: string): string {
  if (
    error &&
    typeof error === 'object' &&
    'response' in error &&
    error.response &&
    typeof error.response === 'object' &&
    'data' in error.response
  ) {
    const data = error.response.data
    if (data && typeof data === 'object' && 'error' in data) {
      const errorObj = data.error
      if (errorObj && typeof errorObj === 'object' && 'message' in errorObj) {
        const msg = errorObj.message
        if (typeof msg === 'string') {
          return msg
        }
      }
    }
  }
  return defaultMessage
}

interface FeatureEditModalProps {
  hospitalId: string
  assignment: FeatureAssignment | null
  open: boolean
  onClose: () => void
}

export const FeatureEditModal = ({
  hospitalId,
  assignment,
  open,
  onClose,
}: FeatureEditModalProps) => {
  const [form] = Form.useForm()
  const [isPreviewMode, setIsPreviewMode] = useState(false)
  const [previewData, setPreviewData] = useState<PreviewFeatureResponse['data'] | null>(null)
  const [isLoadingPreview, setIsLoadingPreview] = useState(false)
  const [validatedValues, setValidatedValues] = useState<{
    value: unknown
    enabled: boolean
  } | null>(null)
  const queryClient = useQueryClient()

  // Initialize form with current values when assignment changes
  useEffect(() => {
    if (assignment && open) {
      // Parse value if it's a JSON string
      let parsedValue = assignment.value
      if (assignment.feature?.data_type === 'json' && typeof assignment.value === 'string') {
        try {
          parsedValue = JSON.parse(assignment.value)
        } catch (error) {
          console.error('Failed to parse JSON value:', error)
          // If parsing fails, use as-is
          parsedValue = assignment.value
        }
      }

      form.setFieldsValue({
        enabled: assignment.enabled,
        value:
          assignment.feature?.data_type === 'json'
            ? JSON.stringify(parsedValue, null, 2)
            : parsedValue,
      })
    }
  }, [assignment, open, form])

  const updateMutation = useMutation({
    mutationFn: (data: { value: unknown; enabled: boolean }) => {
      if (!assignment) throw new Error('No assignment selected')
      return featureAssignmentsApi.updateFeatureAssignment(hospitalId, assignment.featureId, data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feature-assignments', hospitalId] })
      queryClient.invalidateQueries({ queryKey: ['hospital-features', hospitalId] })
      queryClient.invalidateQueries({ queryKey: ['hospital', hospitalId] })
      message.success('Feature updated successfully')
      handleClose()
    },
    onError: (error: unknown) => {
      message.error(getErrorMessage(error, 'Failed to update feature'))
    },
  })

  const handleClose = () => {
    form.resetFields()
    setIsPreviewMode(false)
    setPreviewData(null)
    setValidatedValues(null)
    onClose()
  }

  const handlePreview = async () => {
    try {
      const values = await form.validateFields()

      if (!assignment || !assignment.feature) {
        message.error('Feature information not available')
        return
      }

      // Parse JSON value if data type is json
      let finalValue = values.value
      if (assignment.feature.data_type === 'json' && typeof values.value === 'string') {
        try {
          finalValue = JSON.parse(values.value)
        } catch (error) {
          console.error('Failed to parse JSON value:', error)
          message.error('Invalid JSON value')
          return
        }
      }

      setIsLoadingPreview(true)

      // Store validated values for later submission
      const submitData = {
        value: finalValue,
        enabled: values.enabled ?? true,
      }
      setValidatedValues(submitData)

      const response = await featureAssignmentsApi.previewFeatureAssignment(hospitalId, {
        featureId: assignment.featureId,
        enabled: values.enabled ?? true,
        value: finalValue,
      })

      setPreviewData(response.data)
      setIsPreviewMode(true)
    } catch (error) {
      console.error('Preview error:', error)
      message.error(getErrorMessage(error, 'Failed to generate preview'))
    } finally {
      setIsLoadingPreview(false)
    }
  }

  const handleBackToEdit = () => {
    setIsPreviewMode(false)
  }

  const handleSubmit = async () => {
    try {
      // If in preview mode, use the stored validated values
      if (isPreviewMode && validatedValues) {
        updateMutation.mutate(validatedValues)
        return
      }

      // Otherwise, validate form and submit
      const values = await form.validateFields()

      if (!assignment || !assignment.feature) {
        message.error('Feature information not available')
        return
      }

      // Parse JSON value if data type is json
      let finalValue = values.value
      if (assignment.feature.data_type === 'json' && typeof values.value === 'string') {
        try {
          finalValue = JSON.parse(values.value)
        } catch (error) {
          console.error('Failed to parse JSON value:', error)
          message.error('Invalid JSON value')
          return
        }
      }

      updateMutation.mutate({
        value: finalValue,
        enabled: values.enabled ?? true,
      })
    } catch (error) {
      console.error('Validation failed:', error)
    }
  }

  if (!assignment || !assignment.feature) {
    return null
  }

  return (
    <Modal
      title={isPreviewMode ? 'Preview Feature Update' : 'Edit Feature Assignment'}
      open={open}
      onCancel={handleClose}
      footer={
        isPreviewMode
          ? [
              <Button key="back" onClick={handleBackToEdit} icon={<EditOutlined />}>
                Back to Edit
              </Button>,
              <Button
                key="submit"
                type="primary"
                loading={updateMutation.isPending}
                onClick={handleSubmit}
              >
                Confirm Update
              </Button>,
            ]
          : [
              <Button key="cancel" onClick={handleClose}>
                Cancel
              </Button>,
              <Button
                key="preview"
                type="default"
                icon={<EyeOutlined />}
                onClick={handlePreview}
                loading={isLoadingPreview}
              >
                Preview Changes
              </Button>,
              <Button
                key="submit"
                type="primary"
                onClick={handleSubmit}
                loading={updateMutation.isPending}
              >
                Update Feature
              </Button>,
            ]
      }
      width={isPreviewMode ? 900 : 700}
      destroyOnClose
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Show preview if in preview mode */}
        {isPreviewMode && previewData ? (
          <FeaturePreview preview={previewData} />
        ) : (
          <>
            <Card size="small">
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <div>
                  <Text strong>{assignment.feature.name}</Text>
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Key: <code>{assignment.feature.key}</code>
                  </Text>
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Type: <Tag color="blue">{assignment.feature.data_type}</Tag>
                  </Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Storage: <Tag>{assignment.feature.storage_location}</Tag>
                  </Text>
                </div>
              </Space>
            </Card>

            <Form form={form} layout="vertical">
              <Form.Item name="enabled" label="Enabled" valuePropName="checked">
                <Switch checkedChildren="Enabled" unCheckedChildren="Disabled" />
              </Form.Item>

              <FeatureValueEditor feature={assignment.feature} />
            </Form>
          </>
        )}
      </Space>
    </Modal>
  )
}
